import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  NgZone,
  Renderer2,
  RendererStyleFlags2,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JsonApiQueryData } from '@elearnio/angular2-jsonapi';
import { throttleTime, take } from 'rxjs/operators';

// Models
import { Member } from '@models/member.model';
import { Course } from '@models/course.model';
import { CourseActivity } from '@models/course/course-activity.model';
import { CourseMemberAnswer } from '@models/course/member/course-member-answer.model';

// Services
import { MemberService } from '@services/member.service';
import { CourseService } from '@services/course.service';
import { Datastore } from '@services/datastore';
import { ActivityEventQueueService } from '@services/activity-event-queue.service';
import { CourseMemberAnswerService } from '@services/course-member-answer.service';
import { H5pMediaUrlService } from '@services/h5p-media-url.service';
import {
  H5PEventService,
  IH5PEventMultiResult,
  IH5PEventSingleResult,
} from '@services/h5p-event.service';

// Shared
import { H5PInitialization } from '@shared/h5p/assets/h5p-initialization';
import { H5pRunnerTranslationsService } from '@shared/h5p-translations/h5p-runner-translations.service';
import {
  isLanguageAvailableToLearners,
  resolveLearnerLanguage,
} from '@shared/training-translations/training-availability.util';
import {
  applyTrainingTranslation,
  captureTrainingSource,
  trainingTranslationFields,
} from '@shared/training-translations/training-translation.util';
import { TrainingTranslationFields } from '@models/training-translation.model';
import { environment } from '@environment';

// Constants
import { H5PEvent } from '@core/constants/h5p-events.constant';
import { LANGUAGES } from '@core/constants/language.constants';
import { CourseMemberResult } from '@app/models/course/member/course-member-result.model';
import { ELEARNIO_ACTIVITIES } from '@app/core/constants/elearnio-activities.constant';
import { Subscription, fromEvent } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { PendingRequestsInterceptorConfigurer } from 'ng-http-loader';
import { MediaMatcher } from '@angular/cdk/layout';
import { MatDialog } from '@angular/material/dialog';
import { H5PPlayerTimesUpDialogComponent } from '../h5p-player-timesup/h5p-player-timesup-dialog.component';
import { PermissionService } from '@app/core/services/permission.service';
import { ApiService } from '@app/core/services/api.service';

export interface IH5PEventStatement {
  userId: string;
  courseId: string;
  activityId: string;
  action: string;
  result: IH5PEventMultiResult | IH5PEventSingleResult | null;
}

@Component({
  selector: 'app-h5p-player-standalone-component',
  templateUrl: './h5p-player-standalone.component.html',
  styleUrls: ['h5p-player-standalone.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,

  standalone: false,
})
export class H5pPlayerStandaloneComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('h5pContent') h5pContent!: ElementRef<HTMLDivElement>;
  @ViewChild('runnerHeader', { read: ElementRef })
  readonly runnerHeader?: ElementRef<HTMLElement>;
  readonly sidebarCollapsed = signal(false);
  public multiple: boolean | undefined;
  public displaySpinner = true;
  public displayTimer = false;
  public hasPrevious = false;
  public hasNext = false;
  public showCourseNavigation = false;
  public isLastActivity = false;
  public readonly adminPreview = signal(false);
  public isPreview = false;
  progress = 0;
  index = 0;
  durationInMinutes = 0;
  courseId = '';
  readonly course = signal<Course | undefined>(undefined, {
    equal: () => false, // notify on every write; Course is mutated in-place via courseResult
  });
  readonly freeOrder = computed(
    () => (this.course()?.freeActivitiesOrder ?? false) || this.adminPreview(),
  );
  readonly isCompletedCourse = computed(
    () => this.course()?.courseResult?.progress === 100,
  );
  learningPathId = '';
  showIcon = false;
  showFade = true;
  mobileQuery: MediaQueryList;
  activityElement: HTMLElement | undefined;
  private courseMemberResult!: CourseMemberResult;
  private subscribedToXAPI = false;
  private userId = '';
  private userName = '';
  private userLanguage = '';
  // Explicit language selection from the course overview (?language=). Fixed for
  // the whole runner session — no in-course switching.
  private requestedLanguage = '';
  // Feeds the course navigation panel (table of contents).
  readonly navLanguage = signal('');
  readonly navTranslationFields = signal<TrainingTranslationFields | null>(
    null,
  );
  private profileLanguage = '';
  public currentActivityId = '';
  private currentActivityType = '';
  private courseActivities: CourseActivity[] = [];
  public courseActivitiesAnswers: CourseMemberAnswer[] = [];
  private isActivityCompleted = false;
  private hasCompletedCourse = false;
  private isDestroyedComponent = false;
  private currentH5PInstance: any = null;
  private currentH5PQuizInstance: any = null;
  private activityElementObservable: Subscription = new Subscription();
  private mobileQueryListener: () => void;
  private mutationObserver: MutationObserver = new MutationObserver(() => {});
  private currentActivityTypeStore = '';
  private pendingScormEventStatement: IH5PEventStatement | null = null;
  private ltiPollingInterval: any = null;
  private headerObserver: ResizeObserver | null = null;

  readonly globalSpinnerVisible = toSignal(
    inject(PendingRequestsInterceptorConfigurer).pendingRequestsStatus$,
    { initialValue: false },
  );

  private readonly runnerTranslations = inject(H5pRunnerTranslationsService);

  constructor(
    private datastore: Datastore,
    private route: ActivatedRoute,
    private router: Router,
    private h5pInitialization: H5PInitialization,
    private h5pEventService: H5PEventService,
    private courseService: CourseService,
    private activityEventQueue: ActivityEventQueueService,
    private courseMemberAnswerService: CourseMemberAnswerService,
    private memberService: MemberService,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private el: ElementRef,
    private renderer: Renderer2,
    media: MediaMatcher,
    public dialog: MatDialog,
    private permissionService: PermissionService,
    private apiService: ApiService,
    private h5pMediaUrlService: H5pMediaUrlService,
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 960px)');
    this.mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this.mobileQueryListener);
  }

  ngAfterViewInit(): void {
    const host = this.el.nativeElement as HTMLElement;
    const header =
      this.runnerHeader?.nativeElement.querySelector<HTMLElement>('.header');
    if (!header) {
      return;
    }
    this.headerObserver = new ResizeObserver(() => {
      if (this.isDestroyedComponent) {
        return;
      }
      this.renderer.setStyle(
        host,
        '--runner-header-height',
        `${header.getBoundingClientRect().height}px`,
        RendererStyleFlags2.DashCase,
      );
    });
    this.headerObserver.observe(header);
  }

  ngOnInit(): void {
    this.memberService
      .current()
      .pipe(take(1))
      .subscribe((member: Member | null) => {
        if (!member) {
          this.router.navigateByUrl('courses/my');
        }
        this.router.onSameUrlNavigation = 'reload';
        this.route.queryParams.pipe(take(1)).subscribe(data => {
          if (data.courseId) {
            this.courseId = data.courseId;
            this.userId = member?.id || '';
            this.userName = member?.name || '';
            this.profileLanguage = member?.language || LANGUAGES.DE;
            this.userLanguage = this.profileLanguage;
            this.requestedLanguage = data.language || '';
            // Missing index yields NaN, which crashes loadLibrary.
            const activityIndex = parseInt(data.index, 10);
            this.index = isNaN(activityIndex) ? 0 : activityIndex;
            this.learningPathId = data.learningPathId || '';

            this.setPreviewAndRunContent(data);
          } else {
            this.router.navigateByUrl('courses/my');
          }
        });
      });
    window.onbeforeunload = () => {
      void this.ngOnDestroy();
    };
  }

  public setPreviewAndRunContent(data: any): void {
    if (data.adminPreview || data.isPreview) {
      this.permissionService
        .getPermission('Course', this.courseId)
        .pipe(take(1))
        .subscribe(permission => {
          if (permission?.update) {
            this.adminPreview.set(data.adminPreview);
            this.isPreview = data.adminPreview || data.isPreview;
          }
          this.runContent();
        });
    } else {
      this.runContent();
    }
  }

  public runContent(): void {
    this.startShowContent();
    if (!this.isPreview) {
      this.loadCourseMemberResult();
    }
  }

  public startShowContent(): void {
    this.displaySpinner = true;

    this.datastore
      .findRecord(Course, this.courseId, {
        include: [
          'course_activity_sections',
          'course_activity_sections.course_activities',
          // The runner's visibility guard reads `active` off the translation rows.
          'training_translations',
        ].join(','),
      })
      .pipe(take(1))
      .subscribe((course: Course) => {
        if (this.courseMemberResult) {
          course.courseResult = this.courseMemberResult;
        }
        // An explicit AVAILABLE overview selection wins; otherwise keep the profile default.
        if (
          this.requestedLanguage &&
          isLanguageAvailableToLearners(course, this.requestedLanguage)
        ) {
          this.userLanguage = this.requestedLanguage;
        }
        this.applyCourseTranslation(course);
        this.course.set(course);
        this.maybePersistResultLanguage();
        this.handleCourse();
      });
  }

  /** Training-level display in the selected language, per-field fallback to source. */
  private applyCourseTranslation(course: Course): void {
    applyTrainingTranslation(
      course,
      captureTrainingSource(course),
      resolveLearnerLanguage(course, this.userLanguage),
    );
    this.navTranslationFields.set(
      trainingTranslationFields(course, this.navDisplayLanguage(course)),
    );
  }

  // The language the course navigation panel displays: the selection, guarded
  // against a toggled-OFF language, which collapses to source.
  private navDisplayLanguage(course: Course): string {
    return resolveLearnerLanguage(course, this.userLanguage);
  }

  /** Persist the overview selection to the result; skipped for previews and completed results. */
  private maybePersistResultLanguage(): void {
    const course = this.course();
    const result = this.courseMemberResult;
    const language = this.requestedLanguage;
    if (!course || !result || !language || this.isPreview) {
      return;
    }
    if (result.progress === 100 || !!result.completionDate) {
      return;
    }
    if (!isLanguageAvailableToLearners(course, language)) {
      return;
    }
    if (result.language === language) {
      return;
    }
    result.language = language;
    result.save().pipe(take(1)).subscribe();
  }

  public async initializeH5P(courseActivity: CourseActivity): Promise<void> {
    // @ts-ignore
    window.H5P = { ...window.H5P, preventInit: true };
    await this.h5pInitialization.initGlobalH5P();
    this.h5pInitialization.setupGlobalH5PEditor();

    this.subscribeToXAPI();
    this.loadLibrary(courseActivity);
  }

  public async loadLibrary(courseActivity: CourseActivity): Promise<void> {
    this.setShowCourseNavigation(courseActivity);
    this.isLastActivity = this.index === this.courseActivities.length - 1;
    this.displaySpinner = true;
    this.currentActivityId = courseActivity.id;
    this.currentActivityType = courseActivity.h5pLibrary;
    const usersAnswer =
      this.courseActivitiesAnswers[this.findCurrentActivityAnswerIdx()];

    const contentUserDataObject =
      (usersAnswer && usersAnswer.content && JSON.parse(usersAnswer.content)) ||
      {};
    this.isActivityCompleted = contentUserDataObject.result?.completion;
    let contentUserDataState = contentUserDataObject.result?.state;
    if (contentUserDataState) {
      try {
        contentUserDataState = JSON.stringify(
          Object.assign(JSON.parse(contentUserDataState), {
            completion: contentUserDataObject.result?.completion,
            success: contentUserDataObject.result?.success,
          }),
        );
      } catch {}
    }
    const contentUserData = { 0: { state: contentUserDataState } };

    this.setNavigationArrows(courseActivity.h5pLibrary, null);

    // Get translated content based on user language
    const translatedContent = await courseActivity.getTranslatedContent(
      this.userLanguage,
    );
    const contentWithMediaUrls =
      courseActivity.h5pLibrary === H5PEvent.VERSION.SCORM
        ? translatedContent
        : await this.h5pMediaUrlService.resolveMediaUrls(
            translatedContent,
            courseActivity.contentId,
          );
    const translatedContentJSON = JSON.stringify(contentWithMediaUrls);

    // @ts-ignore
    await globalThis.H5PEditor.loadLibrary(courseActivity.h5pLibrary, () => {
      setTimeout(async () => {
        // H5P invokes this callback outside Angular's zone, so re-enter it to run
        // change detection (otherwise displaySpinner=false won't update the view).
        this.ngZone.run(() => {
          const contentUrlPath =
            courseActivity?.h5pLibrary === H5PEvent.VERSION.SCORM
              ? '/content-scorm/'
              : '/content/';
          this.displaySpinner = false;

          // Base H5P integration content structure
          const baseContent: any = {
            displayOptions: {
              copy: false,
              copyright: false,
              embed: false,
              export: false,
              frame: false,
              icon: false,
            },
            fullScreen: false,
            jsonContent: translatedContentJSON,
            library: courseActivity.h5pLibrary,
            contentUrl: `${environment.h5pBaseUrl}${contentUrlPath}${courseActivity?.contentId}`,
            contentUserData,
            previousChildrenOrder:
              contentUserDataObject?.result?.children?.map(
                (c: any) => c.subContentId,
              ) || [],
            assignmentDate: this.course()?.courseMemberAssignmentDate,
            activityId: courseActivity.id,
            userId: this.userId,
            userName: this.userName,
            userLanguage: this.userLanguage,
            adminPreview: this.adminPreview(),
          };

          // Add LTI data if this is an LTI activity
          if (courseActivity.h5pLibrary === H5PEvent.VERSION.LTI) {
            const ltiLaunchUrl = this.apiService.getLtiLaunchDataUrl(
              courseActivity.id,
            );
            baseContent.lti = {
              launchUrl: ltiLaunchUrl,
            };

            // Start polling for completion if activity is not already completed
            if (!this.isActivityCompleted) {
              this.startLtiCompletionPolling();
            }
          }

          // @ts-ignore
          globalThis.H5PIntegration.contents[
            `cid-${courseActivity.contentId}`
          ] = baseContent;
          this.h5pContent?.nativeElement?.classList?.remove('h5p-initialized');
          this.h5pContent.nativeElement.innerHTML = '';

          // @ts-ignore
          globalThis.H5P.jQuery('.h5p-content:not(.h5p-initialized)').data(
            'content-id',
            courseActivity.contentId,
          );

          // @ts-ignore
          globalThis.H5P.init(globalThis.H5P.jQuery('.h5p-player-container'));

          // Create an observer instance linked to the callback function
          const mutationCb = (mutationsList: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            for (const mutation of mutationsList) {
              if (this.currentActivityType !== this.currentActivityTypeStore) {
                this.showScrollIcon();
                this.currentActivityTypeStore = this.currentActivityType;
              }
            }
          };
          this.mutationObserver = new MutationObserver(mutationCb);
          // Start observing the target node for configured mutations
          this.mutationObserver.observe(this.h5pContent.nativeElement, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: false,
          });
        });
      }, 0);
    });
  }

  get isCurrentActivitySkippable(): boolean {
    const currentActivity = this.courseActivities[this.index];
    if (!currentActivity?.optional) {
      return false;
    }
    if (currentActivity.h5pLibrary === H5PEvent.VERSION.TASK) {
      if (this.currentH5PInstance?.submitted) {
        return false;
      }
      const answerIdx = this.findCurrentActivityAnswerIdx();
      if (answerIdx > -1) {
        try {
          const content = JSON.parse(
            this.courseActivitiesAnswers[answerIdx].content,
          );
          const state = content.result?.state
            ? JSON.parse(content.result.state)
            : null;
          if (state?.submitted) {
            return false;
          }
        } catch {}
      }
    }
    return true;
  }

  onSidebarNavigate(activityId: string): void {
    const idx = this.courseActivities.findIndex(a => a.id === activityId);
    if (idx === -1 || idx === this.index) {
      return;
    }
    this.mutationObserver.disconnect();
    this.stopLtiCompletionPolling();
    this.currentH5PInstance = null;
    this.index = idx;
    this.loadLibrary(this.courseActivities[idx]);
    this.setNavigationArrows(this.courseActivities[idx].h5pLibrary, null);
  }

  previous(): void {
    this.mutationObserver.disconnect();
    this.stopLtiCompletionPolling();
    this.currentH5PInstance = null;
    this.loadLibrary(this.courseActivities[--this.index]);
    this.setNavigationArrows(
      this.courseActivities[this.index].h5pLibrary,
      null,
    );
  }

  async next(): Promise<void> {
    this.mutationObserver.disconnect();
    this.stopLtiCompletionPolling();
    if (this.pendingScormEventStatement) {
      await this.activityEventQueue
        .pushEvent(this.pendingScormEventStatement)
        .toPromise();
      this.pendingScormEventStatement = null;
    }
    if (
      this.currentH5PInstance?.libraryInfo?.versionedName ===
        H5PEvent.VERSION.TASK &&
      (!this.currentH5PInstance.feedback ||
        !!this.currentH5PInstance.changedTask)
    ) {
      this.currentH5PInstance.completeTask('next');
      return;
    }
    this.currentH5PInstance = null;
    this.index++;
    if (this.index === this.courseActivities.length) {
      this.back2Course();
    } else {
      this.loadLibrary(this.courseActivities[this.index]);
      this.setNavigationArrows(
        this.courseActivities[this.index].h5pLibrary,
        null,
      );
    }
  }

  async skip(): Promise<void> {
    if (!this.isCurrentActivitySkippable) {
      return;
    }

    this.mutationObserver.disconnect();
    this.stopLtiCompletionPolling();

    let currentInstance = this.currentH5PInstance;
    if (this.currentH5PInstance?.parent) {
      currentInstance = this.currentH5PInstance.parent;
    }

    // Create skipped answer event
    const skipEventStatement: IH5PEventStatement = {
      userId: this.userId,
      courseId: this.courseId,
      activityId: this.currentActivityId,
      action: 'skipped',
      result: {
        subContentId: '',
        answer: '',
        completion: true,
        success: true,
        duration: 0,
        score: 0,
        maxScore:
          typeof currentInstance?.getMaxScore === 'function'
            ? currentInstance.getMaxScore()
            : -1,
        state: '',
        skipped: true,
      } as IH5PEventSingleResult,
    };

    // Save the skipped answer
    if (!this.course()?.noCompletion && !this.isPreview) {
      await this.activityEventQueue.pushEvent(skipEventStatement).toPromise();
    }

    // Update local state
    const usersAnswerIndex = this.findCurrentActivityAnswerIdx();
    if (usersAnswerIndex > -1) {
      this.courseActivitiesAnswers[usersAnswerIndex].content =
        JSON.stringify(skipEventStatement);
    } else {
      this.courseActivitiesAnswers.push(
        this.datastore.createRecord(CourseMemberAnswer, {
          content: JSON.stringify(skipEventStatement),
        }),
      );
    }

    this.isActivityCompleted = true;
    this.setProgress();
    this.courseService.setCourseProgress(this.courseId, this.progress);

    // Move to next activity
    this.currentH5PInstance = null;
    this.index++;
    if (this.index === this.courseActivities.length) {
      this.back2Course();
    } else {
      this.loadLibrary(this.courseActivities[this.index]);
      this.setNavigationArrows(
        this.courseActivities[this.index].h5pLibrary,
        null,
      );
    }
  }

  back2Course(): void {
    const queryParams = {
      queryParams: {},
    };
    if (this.adminPreview()) {
      queryParams.queryParams = {
        adminPreview: true,
      };
    } else if (this.isPreview) {
      queryParams.queryParams = {
        isPreview: true,
      };
    }

    if (this.learningPathId) {
      Object.assign(queryParams.queryParams, {
        learningPathId: this.learningPathId,
      });
    }

    // Hand the session's explicit language back so the overview keeps it
    // preselected — read live from the URL, the field can go stale.
    const language =
      this.route.snapshot.queryParams.language || this.requestedLanguage;
    if (language) {
      Object.assign(queryParams.queryParams, { language });
    }

    this.router.navigate([`/courses/${this.courseId}/overview`], queryParams);
  }

  async ngOnDestroy(): Promise<void> {
    this.isDestroyedComponent = true;
    this.activityElementObservable.unsubscribe();
    this.mutationObserver.disconnect();
    this.headerObserver?.disconnect();
    this.stopLtiCompletionPolling();
    // @ts-ignore
    if (window.H5P && window.H5P.externalDispatcher) {
      // @ts-ignore
      window.H5P.externalDispatcher.off('xAPI');
    }

    await this.saveInteractiveVideoProgress();
  }

  scrollToBottom(): void {
    if (this.activityElement) {
      this.activityElement.scroll({
        top: this.activityElement.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  timerZero(): void {
    this.dialog
      .open(H5PPlayerTimesUpDialogComponent, {
        disableClose: true,
      })
      .afterClosed()
      .subscribe(() => {
        this.currentH5PQuizInstance.goToResults();
      });
    this.displayTimer = false;
    this.durationInMinutes = 0;
  }

  private async saveInteractiveVideoProgress(): Promise<void> {
    // @ts-ignore
    const h5pInstances = window.H5P?.instances;
    if (
      this.isActivityCompleted ||
      !h5pInstances ||
      !h5pInstances.length || // @ts-ignore
      h5pInstances[h5pInstances.length - 1].libraryInfo.versionedName !==
        H5PEvent.VERSION.VIDEO
    ) {
      return;
    }
    const currentActivity = this.courseActivities.find(
      a => a.id === this.currentActivityId,
    );

    if (currentActivity?.h5pLibrary !== H5PEvent.VERSION.VIDEO) {
      return;
    }

    const videoInstance = h5pInstances[h5pInstances.length - 1];
    await this.parseEvent(
      videoInstance.getXAPIEventData('answered').event,
      videoInstance,
    );
    this.changeNavigationArrows(
      videoInstance.getXAPIEventData('answered').event,
      videoInstance,
    );
  }

  private loadCourseMemberResult(): void {
    this.datastore
      .findAll(CourseMemberResult, {
        filter: {
          latest_result: `${this.courseId},${this.userId}`,
        },
      })
      .pipe(take(1))
      .subscribe(courseMemberResult => {
        const latestResult = courseMemberResult.getModels()[0];

        if (
          !latestResult ||
          (!latestResult.courseMemberId && latestResult.progress < 100)
        ) {
          this.router.navigateByUrl('courses/my');
          return;
        }

        this.courseMemberResult = latestResult;
        const c = this.course();
        if (c) {
          c.courseResult = this.courseMemberResult;
          this.course.update(curr => curr);
        }
        this.maybePersistResultLanguage();
      });
  }

  private handleCourse(): void {
    const c = this.course();
    if (c) {
      this.courseActivities = this.getCourseActivities(c);
      // One batched translation lookup per course visit; the nav panel switches
      // language only once it resolves (section titles apply immediately).
      void this.runnerTranslations
        .preload(
          c,
          this.courseActivities.map(activity => activity.id),
          this.userLanguage,
        )
        .then(() => this.navLanguage.set(this.navDisplayLanguage(c)));
    }

    if (this.courseActivities.length === 0) {
      this.back2Course();
      return;
    }

    const courseActivityIds = this.courseActivities.map(cA => cA.id);
    this.multiple = this.courseActivities.length > 1;

    if (!this.isPreview) {
      this.courseMemberAnswerService
        .select()
        .filterByActivities(courseActivityIds)
        .filterByCourseAndMember(this.courseId, this.userId)
        .request()
        .pipe(take(1))
        .subscribe(
          (courseMemberAnswer: JsonApiQueryData<CourseMemberAnswer>) => {
            this.courseActivitiesAnswers = courseMemberAnswer.getModels();
            this.setActivitiesOrder();
            this.setProgress();
            if (this.progress === 100) {
              this.hasCompletedCourse = true;
            }
            this.initializeH5P(this.courseActivities[this.index]);
          },
        );
    } else {
      this.setActivitiesOrder();
      this.setProgress();
      this.initializeH5P(this.courseActivities[0]);
    }
  }

  private setNavigationArrows(
    versionedLibraryName: string,
    instance: any,
  ): void {
    this.hasNext =
      (!!this.isActivityCompleted ||
        this.h5pEventService.allowNavigation(versionedLibraryName) ||
        this.h5pEventService.isAnsweredTaskActivity(
          versionedLibraryName,
          instance,
        )) &&
      this.courseActivities.length >= this.index + 1;
    this.hasPrevious = this.index > 0;
  }

  private getCourseActivities(course: Course): CourseActivity[] {
    let courseActivities: CourseActivity[] = [];
    for (const section of this.courseService.sortByOrder(
      course.courseActivitiesSections,
    ) || []) {
      courseActivities = courseActivities.concat(
        this.courseService.sortByOrder(section.courseActivities),
      );
    }
    return courseActivities.filter(
      a => a.h5pLibrary !== ELEARNIO_ACTIVITIES.LABEL,
    );
  }

  private subscribeToXAPI(): void {
    if (!this.subscribedToXAPI && !this.isDestroyedComponent) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this; // @ts-ignore
      window.H5P.externalDispatcher.on('xAPI', function (event) {
        if (!event) {
          return;
        }

        // @ts-ignore
        self.currentH5PInstance = this;
        if (self.h5pEventService.showTimerEvent(event)) {
          // @ts-ignore
          self.currentH5PQuizInstance = this;
        }
        // @ts-ignore
        self.handleXAPI(event, this);
      });
      this.subscribedToXAPI = true;
    }
  }

  private async handleXAPI(event: any, instance: any): Promise<void> {
    if (this.h5pEventService.shouldParseEvent(event, instance)) {
      await this.parseEvent(event, instance);
      if (instance.libraryInfo.versionedName !== H5PEvent.VERSION.TASK) {
        // Task activity is handled from the activity itself because
        // it does not have submit buttons
        this.changeNavigationArrows(event, instance);
      }
    } else if (this.h5pEventService.isShowCustomNavigationEvent(event)) {
      const currentActivity: CourseActivity | null = this.datastore.peekRecord(
        CourseActivity,
        this.currentActivityId,
      );
      if (currentActivity) {
        this.setShowCourseNavigation(currentActivity);
      }
    } else if (this.h5pEventService.isShowBaseNavigationEvent(event)) {
      this.showCourseNavigation = true;
    } else if (this.h5pEventService.isResetEvent(event)) {
      this.resetAnswer();
      this.isActivityCompleted = false;
      this.setNavigationArrows(instance.libraryInfo.versionedName, instance);
    } else if (this.h5pEventService.isSkipEvent(event)) {
      this.next();
    } else if (this.h5pEventService.isExitEvent(event)) {
      if (instance.activityId === this.currentActivityId) {
        this.back2Course();
      }
    } else if (this.h5pEventService.isEnableNavigationEvent(event)) {
      this.hasNext = true;
      this.hasPrevious = this.index > 0;
    } else if (this.h5pEventService.isDisableNavigationEvent(event)) {
      this.hasNext = false;
      this.hasPrevious = false;
    } else if (this.h5pEventService.isNextEvent(event)) {
      this.currentH5PInstance = null;
      if (
        !this.course()?.freeActivitiesOrder &&
        instance.libraryInfo?.versionedName === H5PEvent.VERSION.TASK
      ) {
        this.back2Course();
        return;
      }
      this.next();
    } else if (this.h5pEventService.isPrevEvent(event)) {
      this.previous();
    } else if (this.h5pEventService.showScrollIconEvent(event)) {
      // Special handling for drag text dialog close - restore previous scroll state
      if (event.data.statement.elementClass === 'question-container') {
        this.restoreScrollIconAfterDialog();
      } else {
        this.showScrollIcon(event.data.statement.elementClass);
      }
      if (
        this.currentActivityType === H5PEvent.VERSION.VIDEO ||
        (this.currentActivityType === H5PEvent.VERSION.IMAGE_HOTSPOTS &&
          event.data.statement.scrollForHotspot)
      ) {
        const container = this.el.nativeElement.querySelector('.h5p-container');
        const scrollFade = this.el.nativeElement.querySelector(
          '#h5p-scroll-fade-footer',
        );
        if (container && scrollFade) {
          this.renderer.appendChild(container, scrollFade);
        }
      }
    } else if (this.h5pEventService.hideScrollIconEvent(event)) {
      this.hideScrollIcon();
    } else if (this.h5pEventService.showTimerEvent(event)) {
      this.displayTimer = true;
      this.durationInMinutes = event.data.statement.durationInMinutes;
      const eventStatement: IH5PEventStatement = await this.parseEvent(
        event,
        instance,
      );
      this.checkTimeLeft(eventStatement);
    } else if (this.h5pEventService.hideTimerEvent(event)) {
      this.displayTimer = false;
      this.durationInMinutes = 0;
    }
  }

  private async parseEvent(
    event: any,
    instance: any,
  ): Promise<IH5PEventStatement> {
    const eventStatement: IH5PEventStatement = {
      userId: this.userId,
      courseId: this.courseId,
      activityId: this.currentActivityId,
      action: event.getVerb(), // @ts-ignore
      result: this.h5pEventService.parseEvent(event, instance),
    };
    if (!this.isActivityCompleted) {
      this.isActivityCompleted = eventStatement.result?.completion || false;
    }
    const usersAnswerIndex = this.findCurrentActivityAnswerIdx();

    if (usersAnswerIndex > -1) {
      this.courseActivitiesAnswers[usersAnswerIndex].content =
        JSON.stringify(eventStatement);
      if (instance.libraryInfo?.versionedName !== H5PEvent.VERSION.TASK) {
        this.setProgress();
      }
    } else {
      this.courseActivitiesAnswers.push(
        this.datastore.createRecord(CourseMemberAnswer, {
          content: JSON.stringify(eventStatement),
        }),
      );
      if (eventStatement.result?.completion) {
        this.progress = Number(
          Math.round(
            (this.getCompletedAnswersNumber() * 100) /
              this.courseActivities.length,
          ),
        );
        this.courseService.setCourseProgress(this.courseId, this.progress);
      }
    }

    const c = this.course();
    const isScormCompletion =
      !c?.noCompletion &&
      !this.isPreview &&
      instance.libraryInfo?.versionedName === H5PEvent.VERSION.SCORM &&
      this.progress === 100 &&
      eventStatement.result?.completion === true;
    if (isScormCompletion) {
      this.pendingScormEventStatement = eventStatement;
    }

    if (
      !c?.noCompletion &&
      !this.isPreview &&
      (!c?.courseResult || c.courseResult.progress < 100) &&
      !this.hasCompletedCourse
    ) {
      if (this.progress === 100) {
        this.hasCompletedCourse = true;
      }
      this.setCourseRating(event, instance);
      if (isScormCompletion) {
        // Update navigation arrows to enable the Complete button for SCORM modules
        this.setNavigationArrows(instance.libraryInfo.versionedName, instance);
        this.changeDetectorRef.detectChanges();
      } else {
        await this.activityEventQueue.pushEvent(eventStatement).toPromise();
      }
    }

    if (event?.data?.options?.goNext) {
      this.next();
    }

    return eventStatement;
  }

  private changeNavigationArrows(event: any, instance: any): void {
    this.setNavigationArrows(instance.libraryInfo.versionedName, instance);
    const currentActivity = this.courseActivities.find(
      a => a.id === this.currentActivityId,
    );
    if (
      this.h5pEventService.isCompletedCustomNavigationActivity(
        instance.libraryInfo.versionedName,
        event.getVerb(),
      ) &&
      (!currentActivity ||
        !this.h5pEventService.isCustomNavigationActivity(
          currentActivity.h5pLibrary,
          this.mobileQuery.matches,
        ) ||
        currentActivity.id === instance.activityId)
    ) {
      this.showCourseNavigation = true;
    }
    this.changeDetectorRef.detectChanges();
  }

  private setCourseRating(event: any, instance: any): void {
    if (
      this.courseMemberResult &&
      this.h5pEventService.isCompletedFeedbackActivity(
        instance.libraryInfo.versionedName,
        event.getVerb(),
      )
    ) {
      const courseRating = this.h5pEventService.getRatingStarFeedback(instance);
      if (courseRating) {
        this.courseMemberResult.courseRating = courseRating;
        this.courseMemberResult.save().pipe(take(1)).subscribe();
      }
    }
  }

  private resetAnswer(): void {
    const usersAnswerIndex = this.findCurrentActivityAnswerIdx();

    if (usersAnswerIndex > -1) {
      this.courseActivitiesAnswers.splice(usersAnswerIndex, 1);
      this.setProgress();
    }
  }

  private findCurrentActivityAnswerIdx(): number {
    return this.courseActivitiesAnswers
      .map(a => JSON.parse(a.content))
      .findIndex(a => a.activityId === this.currentActivityId);
  }

  private setProgress(): void {
    const currentProgress =
      this.getCompletedAnswersNumber() / this.courseActivities.length;
    this.progress = Number(Math.round(currentProgress * 100));
  }

  private setShowCourseNavigation(courseActivity: CourseActivity): void {
    if (!courseActivity) {
      return;
    }
    this.showCourseNavigation =
      !this.h5pEventService.isCustomNavigationActivity(
        courseActivity.h5pLibrary,
        this.mobileQuery.matches,
      );
  }

  private getCompletedAnswersNumber(): number {
    return this.courseActivitiesAnswers.reduce((acc, val) => {
      try {
        const answer = JSON.parse(val.content);
        if (answer && answer.result && answer.result.completion) {
          acc += 1;
        }
      } catch {}

      return acc;
    }, 0);
  }

  private setActivitiesOrder(): void {
    if (this.freeOrder()) {
      this.setFreeActivitiesOrder();
    } else {
      this.setSequenceActivitiesOrder();
    }
  }

  private setFreeActivitiesOrder(): void {
    const currentIndex = this.index;
    const nextActivities = this.courseActivities.splice(
      currentIndex,
      this.courseActivities.length - currentIndex,
    );
    this.courseActivities = nextActivities.concat(this.courseActivities);
    this.index = 0;
  }

  private setSequenceActivitiesOrder(): void {
    if (this.isCompletedCourse()) {
      return;
    }

    const answersContent = this.courseActivitiesAnswers.map(a =>
      JSON.parse(a.content),
    );

    let firstAnsweredActivity = this.courseActivities.findIndex(cA => {
      const activityAnswer = answersContent.find(a => cA.id === a.activityId);
      return !activityAnswer || !activityAnswer.result.completion;
    });

    if (firstAnsweredActivity === -1) {
      firstAnsweredActivity = isNaN(this.index) ? 0 : this.index;
    }

    if (isNaN(this.index) || firstAnsweredActivity < this.index) {
      this.index = firstAnsweredActivity;
    }
  }

  private showScrollIcon(innerElementClass = ''): void {
    if (!this.mobileQuery.matches) {
      return;
    }
    this.activityElementObservable.unsubscribe();
    this.activityElementObservable = new Subscription();
    this.activityElement = this.getCurrentActivityElement(innerElementClass);
    if (!this.activityElement) {
      return;
    }
    if (innerElementClass === 'h5p-drag-draggables-container') {
      this.showFade = false;
    }
    if (this.activityElement.scrollTop > 0) {
      this.showIcon =
        Math.ceil(this.activityElement.scrollTop) <
        this.activityElement.scrollHeight - this.activityElement.offsetHeight;
    } else {
      this.showIcon =
        this.activityElement.scrollHeight > this.activityElement.clientHeight;
    }
    this.addActivityElementObservable();
  }

  private addActivityElementObservable(): void {
    this.activityElementObservable.add(
      fromEvent(this.activityElement!, 'scroll')
        .pipe(throttleTime(200, undefined, { leading: true, trailing: true }))
        .subscribe(event => {
          if (event.target) {
            const target = event.target as HTMLElement;
            this.showIcon =
              Math.ceil(target.scrollTop) <
              target.scrollHeight - target.offsetHeight;
          }
        }),
    );
  }

  private hideScrollIcon(): void {
    this.activityElementObservable.unsubscribe();
    this.activityElement = undefined;
    this.showIcon = false;
    this.showFade = true;
  }

  private restoreScrollIconAfterDialog(): void {
    if (!this.mobileQuery.matches) {
      return;
    }
    // .h5p-player-container is the scroller (.h5p-container is overflow:visible on
    // mobile so the fixed footer paints). Restore the indicator against it.
    const h5pContainer = this.el.nativeElement.querySelector(
      '.h5p-player-container',
    );
    if (!h5pContainer) {
      return;
    }

    this.activityElementObservable.unsubscribe();
    this.activityElementObservable = new Subscription();
    this.activityElement = h5pContainer;
    this.showFade = true;

    // Check if container is scrollable and set showIcon accordingly
    if (this.activityElement && this.activityElement.scrollTop > 0) {
      this.showIcon =
        Math.ceil(this.activityElement.scrollTop) <
        this.activityElement.scrollHeight - this.activityElement.offsetHeight;
    } else if (this.activityElement) {
      this.showIcon =
        this.activityElement.scrollHeight > this.activityElement.clientHeight;
    }

    this.addActivityElementObservable();
  }

  private getCurrentActivityElement(
    innerElementClass: string,
  ): HTMLElement | undefined {
    // The quiz scroller is .h5p-player-container (.h5p-container is overflow:visible
    // on mobile so the fixed footer can paint). Point the scroll indicator at it.
    if (!innerElementClass || innerElementClass === 'questionset') {
      return this.el.nativeElement.querySelector('.h5p-player-container');
    }
    if (innerElementClass) {
      const interactionOuter =
        this.el.nativeElement.getElementsByClassName(innerElementClass);
      for (const element of interactionOuter) {
        if (
          element.scrollHeight > element.clientHeight &&
          element.checkVisibility()
        ) {
          return element;
        }
      }
    } else {
      return this.el.nativeElement.getElementsByClassName('h5p-container')[0];
    }
  }

  private checkTimeLeft(eventStatement: IH5PEventStatement): void {
    if (eventStatement && eventStatement.result?.state) {
      const currentState = JSON.parse(eventStatement.result?.state);
      if (currentState && currentState.startTime) {
        const currentTime = Date.now();
        const currentDateTime = new Date(currentTime);
        const startTime = new Date(currentState.startTime);
        const diffTime = new Date(currentTime - currentState.startTime);

        if (
          currentDateTime.getFullYear() === startTime.getFullYear() &&
          currentDateTime.getMonth() === startTime.getMonth() &&
          currentDateTime.getDate() === startTime.getDate()
        ) {
          if (
            diffTime.getMinutes() * 60 + diffTime.getSeconds() >
            this.durationInMinutes * 60
          ) {
            this.durationInMinutes = 0;
          } else {
            this.durationInMinutes =
              this.durationInMinutes -
              (diffTime.getMinutes() + diffTime.getSeconds() / 60);
          }
        }
      }
    }
  }

  private startLtiCompletionPolling(): void {
    this.stopLtiCompletionPolling();

    this.ltiPollingInterval = setInterval(() => {
      this.checkLtiCompletion();
    }, 5000);
  }

  private stopLtiCompletionPolling(): void {
    if (this.ltiPollingInterval) {
      clearInterval(this.ltiPollingInterval);
      this.ltiPollingInterval = null;
    }
  }

  private checkLtiCompletion(): void {
    if (this.isActivityCompleted) {
      this.stopLtiCompletionPolling();
      return;
    }

    this.courseMemberAnswerService
      .select()
      .filterByActivities([this.currentActivityId])
      .filterByCourseAndMember(this.courseId, this.userId)
      .skipLoader()
      .request()
      .pipe(take(1))
      .subscribe((data: JsonApiQueryData<CourseMemberAnswer>) => {
        const answers = data.getModels();
        if (answers && answers.length > 0) {
          const currentAnswer = answers[0];
          const contentUserDataObject =
            (currentAnswer &&
              currentAnswer.content &&
              JSON.parse(currentAnswer.content)) ||
            {};

          if (
            contentUserDataObject?.result?.completion &&
            !this.isActivityCompleted
          ) {
            this.handleLtiCompletion(currentAnswer);
          }
        }
      });
  }

  private handleLtiCompletion(answer: CourseMemberAnswer): void {
    this.isActivityCompleted = true;

    this.stopLtiCompletionPolling();

    const usersAnswerIndex = this.findCurrentActivityAnswerIdx();
    if (usersAnswerIndex > -1) {
      this.courseActivitiesAnswers[usersAnswerIndex] = answer;
    } else {
      this.courseActivitiesAnswers.push(answer);
    }

    this.setProgress();

    const currentActivity = this.courseActivities.find(
      a => a.id === this.currentActivityId,
    );
    if (currentActivity) {
      this.setNavigationArrows(currentActivity.h5pLibrary, null);
    }

    this.changeDetectorRef.detectChanges();
  }
}
